import { MCP_TOOLS } from "@flowstepdesign/shared";
import { ArrowRight } from "lucide-react";
import { motion, useInView } from "motion/react";
import { useEffect, useRef, useState } from "react";

import { MCP_DOCS_URL, MCP_TOOL_COUNT } from "./Mcp/mcpServer";
import { useReducedMotion } from "./ui/use-reduced-motion";
const manualEditsVideo = "/videos/BuildBetterProducts/ManualEdits.mp4";
const copyToFigmaVideo = "/videos/BuildBetterProducts/CopyToFigma.mp4";
const designUsingReferencesVideo =
  "/videos/BuildBetterProducts/DesignUsingReferences.mp4";
const importComponentVideo = "/videos/BuildBetterProducts/ImportComponent.mp4";
const multiplayerVideo = "/videos/BuildBetterProducts/Multiplayer.mp4";
const prototypeVideo = "/videos/BuildBetterProducts/Prototype.mp4";
const designGuidelinesVideo =
  "/videos/BuildBetterProducts/DesignGuidelines.mp4";

type FeatureDef = {
  title: string;
  subtitle: string;
  linkText: string;
  linkHref: string;
  videoSrc?: string;
  mockup?: "guidelines" | "mcp";
};

const features: FeatureDef[] = [
  {
    title: "Not just static designs",
    subtitle:
      "Everything you design in Flowstep is 1:1 with code. Preview interactive designs in seconds — or export the code and ship.",
    linkText: "Learn about interactive prototypes",
    linkHref: "https://docs.flowstep.ai/prototypes",
    videoSrc: prototypeVideo,
  },
  {
    title: "Edit with AI or by hand",
    subtitle:
      "Chat to change anything, or click an element and edit it directly on the canvas. Iterate as many times as you need — nothing drifts out of style.",
    linkText: "Learn about editing",
    linkHref: "https://docs.flowstep.ai/editing-designs",
    videoSrc: manualEditsVideo,
  },
  {
    title: "Design from a PRD, image, or link",
    subtitle:
      "Attach a spec, drop in a screenshot, or paste your product's URL. Flowstep reads the context and designs to match — no lengthy prompts.",
    linkText: "Learn about design references",
    linkHref: "https://docs.flowstep.ai/design-references",
    videoSrc: designUsingReferencesVideo,
  },
  {
    title: "Set your style once",
    subtitle:
      "Your theme holds the colors and rounding; file instructions hold everything else — product rules, tone, layout conventions. Every generation and edit follows both, without repeating them in each prompt.",
    linkText: "Learn about file instructions",
    linkHref: "https://docs.flowstep.ai/design-guidelines",
    videoSrc: designGuidelinesVideo,
  },
  {
    title: "Design from your IDE",
    subtitle:
      "Flowstep's MCP server connects Cursor, Claude Code, Windsurf, or Codex. Generate screens, edit designs, and keep file instructions in sync without leaving your editor.",
    linkText: "Learn about the MCP server",
    linkHref: MCP_DOCS_URL,
    videoSrc: importComponentVideo,
  },
  {
    title: "Copy to Figma — or from it",
    subtitle:
      "Paste any Flowstep frame into Figma with ⌘⇧C and ⌘V, or import Figma frames as fully editable screens. No plugin or extension needed.",
    linkText: "Learn more about Figma integration",
    linkHref: "https://docs.flowstep.ai/figma-integration",
    videoSrc: copyToFigmaVideo,
  },
  {
    title: "Create together, anywhere",
    subtitle:
      "Design with your team in real time — live cursors, edits in sync — then share a clickable prototype anyone can walk through.",
    linkText: "Learn about sharing",
    linkHref: "https://docs.flowstep.ai/sharing-designs",
    videoSrc: multiplayerVideo,
  },
];

// Subtitle gag: the line types out, then "Artificial" is struck through and
// "Artisanal Intelligence" gets the rainbow treatment.
const SUB_PLAIN = "Design with ";
const SUB_STRUCK = "Artificial";
const SUB_RAINBOW = " Artisanal Intelligence";
const SUB_TEXT = SUB_PLAIN + SUB_STRUCK + SUB_RAINBOW;
const SUB_MS_PER_CHAR = 30;

const RAINBOW_GRADIENT =
  "linear-gradient(90deg, #0247FE 0%, #8B5CF6 20%, #EC4899 40%, #F59E0B 60%, #10B981 80%, #0247FE 100%)";

export default function Features() {
  const prefersReducedMotion = useReducedMotion();
  const subtitleRef = useRef<HTMLParagraphElement | null>(null);
  const subtitleInView = useInView(subtitleRef, { once: true, amount: 0.6 });
  const [subTyped, setSubTyped] = useState(0);
  const [struck, setStruck] = useState(false);

  useEffect(() => {
    if (!subtitleInView) return;
    if (prefersReducedMotion) {
      setSubTyped(SUB_TEXT.length);
      setStruck(true);
      return;
    }
    const timers: number[] = [];
    const at = (ms: number, fn: () => void) =>
      timers.push(window.setTimeout(fn, ms));
    // Type up to "…Artificial", strike it through, then type the correction.
    const strikeAfter = (SUB_PLAIN + SUB_STRUCK).length;
    for (let i = 1; i <= strikeAfter; i++) {
      at(400 + i * SUB_MS_PER_CHAR, () => setSubTyped(i));
    }
    const strikeMs = 400 + strikeAfter * SUB_MS_PER_CHAR + 350;
    at(strikeMs, () => setStruck(true));
    const resumeMs = strikeMs + 550;
    for (let i = strikeAfter + 1; i <= SUB_TEXT.length; i++) {
      at(resumeMs + (i - strikeAfter) * SUB_MS_PER_CHAR, () => setSubTyped(i));
    }
    return () => timers.forEach(clearTimeout);
  }, [subtitleInView, prefersReducedMotion]);

  const plainTyped = SUB_PLAIN.slice(0, subTyped);
  const struckTyped = SUB_STRUCK.slice(
    0,
    Math.max(0, subTyped - SUB_PLAIN.length),
  );
  const rainbowTyped = SUB_RAINBOW.slice(
    0,
    Math.max(0, subTyped - SUB_PLAIN.length - SUB_STRUCK.length),
  );

  return (
    <section className="relative w-full">
      {/* Section Header */}
      <div className="relative mx-auto max-w-[1600px] px-4 pt-16 pb-16 text-center md:px-8 lg:px-16 lg:pt-28">
        <motion.h2
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={
            prefersReducedMotion ? { duration: 0 } : { duration: 0.6 }
          }
          className="mb-6 leading-[0.95] tracking-tight text-gray-900 dark:text-white"
          style={{
            fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)",
            fontWeight: "600",
          }}
        >
          Build better products
        </motion.h2>
        <motion.p
          ref={subtitleRef}
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : { duration: 0.6, delay: 0.1 }
          }
          className="relative mx-auto inline-block max-w-3xl leading-relaxed text-gray-600 dark:text-gray-300"
          style={{ fontSize: "clamp(1.125rem, 2.5vw, 1.5rem)" }}
        >
          {/* Full line for SEO and screen readers; the typewriter below is
              presentational. */}
          <span className="sr-only">{SUB_TEXT}</span>
          <span aria-hidden className="relative block">
            {/* Invisible full line reserves the layout while typing. */}
            <span className="invisible">{SUB_TEXT}</span>
            <span className="absolute inset-0">
              {plainTyped}
              <span className="relative inline-block">
                {struckTyped}
                <motion.span
                  aria-hidden
                  initial={false}
                  animate={{ scaleX: struck ? 1 : 0 }}
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                  className="absolute top-[52%] left-0 h-[0.09em] w-full origin-left rounded-full bg-current"
                />
              </span>
              <motion.span
                style={{
                  backgroundImage: RAINBOW_GRADIENT,
                  backgroundSize: "200% 100%",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
                animate={
                  struck && !prefersReducedMotion
                    ? { backgroundPosition: ["0% 0", "-200% 0"] }
                    : { backgroundPosition: "0% 0" }
                }
                transition={{
                  duration: 2.5,
                  repeat: struck && !prefersReducedMotion ? Infinity : 0,
                  ease: "linear",
                }}
              >
                {rainbowTyped}
              </motion.span>
            </span>
          </span>
        </motion.p>
      </div>

      {features.map((feature, index) => (
        <Feature
          key={feature.title}
          {...feature}
          videoThenText={index % 2 === 0}
        />
      ))}
    </section>
  );
}

type FeatureProps = FeatureDef & {
  videoThenText: boolean;
};

function Feature({
  title,
  subtitle,
  videoSrc,
  mockup,
  linkText,
  linkHref,
  videoThenText,
}: FeatureProps) {
  const prefersReducedMotion = useReducedMotion();
  const sign = videoThenText ? 1 : -1;

  return (
    <div className="relative mx-auto max-w-[1600px] px-4 py-12 md:px-8 lg:px-16 lg:py-20">
      <div
        className="order-first flex flex-col items-center gap-16 lg:grid lg:grid-cols-5"
        style={{ direction: videoThenText ? "ltr" : "rtl" }}
      >
        {/* Demo */}
        <motion.div
          initial={prefersReducedMotion ? {} : { opacity: 0, x: -40 * sign }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={
            prefersReducedMotion ? { duration: 0 } : { duration: 0.6 }
          }
          className="group relative mx-auto w-full max-w-lg lg:col-span-3 lg:max-w-none"
          style={{ direction: "ltr" }}
        >
          {videoSrc ? (
            <LazyVideo src={videoSrc} title={title} />
          ) : mockup === "guidelines" ? (
            <DesignGuidelinesMockup />
          ) : (
            <McpTerminalMockup />
          )}
        </motion.div>

        {/* Text */}
        <motion.div
          initial={{ opacity: 0, x: 40 * sign }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="w-full space-y-6 lg:col-span-2"
          style={{ direction: "ltr" }}
        >
          <h3
            className="mb-4 leading-[1.15] tracking-tight text-gray-900 dark:text-white"
            style={{
              fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
              fontWeight: "600",
            }}
          >
            {title}
          </h3>
          <p
            className="mb-6 leading-relaxed text-gray-600 dark:text-gray-300"
            style={{ fontSize: "clamp(1rem, 2vw, 1.125rem)" }}
          >
            {subtitle}
          </p>
          <a
            href={linkHref}
            target="_blank"
            rel="noopener"
            className="inline-flex cursor-pointer items-center gap-2 text-lg font-medium text-[#0247FE] transition-all hover:font-bold dark:text-[#6b96ff]"
          >
            {linkText}
            <ArrowRight className="h-5 w-5" />
          </a>
        </motion.div>
      </div>
    </div>
  );
}

function MockupWindow({
  titleBarLabel,
  children,
}: {
  titleBarLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative rounded-3xl bg-gradient-to-br from-gray-800 to-gray-900 p-6 shadow-2xl lg:p-12">
      <div className="flex aspect-[16/10] flex-col rounded-2xl bg-gray-950 p-6 lg:p-10">
        <div className="mb-6 flex items-center gap-3 lg:mb-8">
          <div className="h-3.5 w-3.5 rounded-full bg-red-500" />
          <div className="h-3.5 w-3.5 rounded-full bg-yellow-500" />
          <div className="h-3.5 w-3.5 rounded-full bg-green-500" />
          <span className="ml-auto font-mono text-xs text-gray-500 lg:text-sm">
            {titleBarLabel}
          </span>
        </div>
        <div className="min-h-0 flex-1 space-y-2.5 overflow-hidden font-mono text-xs leading-relaxed lg:space-y-3.5 lg:text-base">
          {children}
        </div>
      </div>
    </div>
  );
}

function DesignGuidelinesMockup() {
  return (
    <MockupWindow titleBarLabel="DESIGN.md">
      <div className="text-gray-500">## Colors</div>
      <div className="flex items-center gap-2 text-gray-300">
        primary:
        <span className="inline-block h-3 w-3 rounded-sm bg-[#0247FE]" />
        <span className="text-blue-400">#0247FE</span>
      </div>
      <div className="flex items-center gap-2 text-gray-300">
        surface:
        <span className="inline-block h-3 w-3 rounded-sm border border-gray-700 bg-[#05060a]" />
        <span className="text-blue-400">#05060A</span>
      </div>
      <div className="text-gray-500">## Typography</div>
      <div className="text-gray-300">
        Inter — <span className="text-green-400">14 / 16 / 24</span>
      </div>
      <div className="text-gray-500">## Components</div>
      <div className="text-gray-300">
        Buttons are pill-shaped. Primary actions use{" "}
        <span className="text-blue-400">primary</span>.
      </div>
      <div className="text-gray-600">
        Every generation follows these rules ✓
      </div>
    </MockupWindow>
  );
}

function McpTerminalMockup() {
  return (
    <MockupWindow titleBarLabel="cursor — agent">
      <div className="text-gray-300">
        <span className="text-gray-500">$</span> claude mcp add flowstep
        api.flowstep.ai/mcp
      </div>
      <div className="text-green-400">
        ✓ Connected — {MCP_TOOL_COUNT} tools available
      </div>
      <div className="text-gray-300">
        <span className="text-blue-400">&gt;</span> Design an onboarding flow
        for the fitness app
      </div>
      <div className="text-gray-500">⚡ {MCP_TOOLS.aiDesignScreens.name}…</div>
      <div className="text-green-400">
        ✓ 4 screens generated — open in Flowstep
      </div>
    </MockupWindow>
  );
}

function LazyVideo({ src, title }: { src: string; title: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShouldMount(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldMount(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="aspect-video w-full">
      {shouldMount && (
        <video
          src={src}
          loop
          preload="metadata"
          muted
          playsInline
          autoPlay
          aria-label={title}
          className="h-full w-full rounded-3xl object-cover object-center shadow-2xl"
        />
      )}
    </div>
  );
}
